import { useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  authApi,
  setToken,
  setRefreshToken,
  clearToken,
  clearRefreshToken,
  isAuthenticated,
  isTokenExpiringSoon,
} from '@/api/auth';

// 模块级单例定时器 + 引用计数：全局只建一次刷新定时器；
// 任一 useAuth 实例卸载只减计数，最后一个实例卸载才真正清理——
// 此前 cleanup 无条件 clearInterval，任一实例卸载后整 session 再无主动刷新
let refreshTimer: ReturnType<typeof setInterval> | null = null;
let refreshTimerUsers = 0;

export const useAuth = () => {
  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading: isLoadingUser,
    error: userError,
  } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const response = await authApi.me();
      return response.data;
    },
    enabled: isAuthenticated(),
    // 401/403 是确定性失败（token 失效），重试无意义；
    // 网络抖动/5xx/启动竞态是瞬时失败，必须重试——否则一次失败就把用户
    // 永久卡在欢迎页（user 查询不刷新、守卫静默弹回、按钮看似失灵）
    retry: (failureCount, error: any) => {
      const status = error?.response?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 4;
    },
    retryDelay: (attempt) => 1500 * (attempt + 1),
    staleTime: 5 * 60 * 1000, // 5 min
    refetchOnWindowFocus: true, // 瞬时失败缓存后，聚焦窗口即自愈
    refetchOnReconnect: false,
  });

  // Token auto-refresh: 模块级单例定时器（refcount 管理），避免多组件各建一个竞态刷新
  useEffect(() => {
    if (!isAuthenticated()) return;

    refreshTimerUsers += 1;
    if (!refreshTimer) {
      refreshTimer = setInterval(async () => {
        if (!isAuthenticated()) return;
        if (isTokenExpiringSoon(30)) {
          try {
            const response = await authApi.refresh();
            setToken(response.data.access_token);
            if (response.data.refresh_token) setRefreshToken(response.data.refresh_token);
            queryClient.invalidateQueries({ queryKey: ['user'] });
          } catch (err: any) {
            // 仅认证错误（401/403）才登出；网络抖动保留会话
            if (err?.response?.status === 401 || err?.response?.status === 403) {
              clearToken();
              clearRefreshToken();
              queryClient.clear();
              window.location.href = '/welcome';
            }
          }
        }
      }, 5 * 60 * 1000); // 5 minutes
    }

    return () => {
      refreshTimerUsers -= 1;
      // 只引用归零（所有 useAuth 实例都已卸载）才清理共享定时器
      if (refreshTimerUsers <= 0 && refreshTimer) {
        clearInterval(refreshTimer);
        refreshTimer = null;
        refreshTimerUsers = 0;
      }
    };
  }, [queryClient]);

  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (response, variables) => {
      const persistent = !!variables.remember_me;
      setToken(response.data.access_token, persistent);
      if (response.data.refresh_token) setRefreshToken(response.data.refresh_token, persistent);
      // 登录成功即清本地匿名快照：登录后以服务端会话为准，
      // 且共享设备上不得把上一使用者的本地记录带入新账号
      localStorage.removeItem('chatLocalHistory');
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
  });

  const registerMutation = useMutation({
    mutationFn: authApi.register,
    onSuccess: (response) => {
      setToken(response.data.access_token);
      if (response.data.refresh_token) setRefreshToken(response.data.refresh_token);
      localStorage.removeItem('chatLocalHistory');
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
  });

  const sendCodeMutation = useMutation({
    mutationFn: authApi.sendVerificationCode,
  });

  const logout = useCallback(() => {
    authApi.logout().catch(() => {}); // best-effort server-side logout
    clearToken();
    clearRefreshToken();
    // 登出即清本地聊天快照：共享设备上不得把本账号记录留给下一个人
    localStorage.removeItem('chatLocalHistory');
    if ('caches' in window) {
      caches.keys().then((keys) => Promise.all(
        keys.filter((key) => key.startsWith('molore-apiread-')).map((key) => caches.delete(key)),
      )).catch(() => {});
    }
    queryClient.clear();
    window.location.reload();
  }, [queryClient]);

  const isLoggedIn = !!isAuthenticated() && !!user;

  return {
    user: user ?? null,
    isLoggedIn,
    isLoading: isLoadingUser,
    isLoadingUser,
    /** user 查询的失败对象（429/5xx/网络抖动时 AuthGuard 据此不踢人） */
    userError,
    login: loginMutation.mutateAsync,
    register: registerMutation.mutateAsync,
    sendCode: sendCodeMutation.mutateAsync,
    logout,
    loginError: loginMutation.error,
    registerError: registerMutation.error,
    sendCodeError: sendCodeMutation.error,
    isLoggingIn: loginMutation.isPending,
    isRegistering: registerMutation.isPending,
    isSendingCode: sendCodeMutation.isPending,
    refreshUser: useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
    }, [queryClient]),
  };
};
